import * as React from 'react'
import PropTypes from 'prop-types'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Alert from '@material-ui/lab/Alert'
import Dialog from '@material-ui/core/Dialog'
import Pagination from '@material-ui/lab/Pagination'
import Rating from '@material-ui/lab/Rating'
import Slide from '@material-ui/core/Slide'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { Formit } from '@oakwood/formit'
import { useRouter } from 'next/router'
import { useReviews, submitProductReview } from 'api'
import Container from 'components/Container'
import Link from 'components/Link'
import Section from 'components/Section'
import Typography from 'components/Typography'
import Button from 'components/Button'
import { useTranslations } from 'containers/Translations/TranslationsContext'
import ReviewsForm from './partials/ReviewsForm'

const BREAKPOINT_KEY_UP = 'sm'

const TransitionComponent = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
))

export const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: -1,
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  container: {},
  header: {
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: {
      display: 'flex',
      justifyContent: 'space-between',
    },
  },
  heading: {},
  reviewInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    maxWidth: 250,
    margin: theme.spacing(-0.5),
    '& > *': {
      margin: theme.spacing(0.5),
    },
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: {
      justifyContent: 'flex-end',
    },
  },
  main: {
    display: 'grid',
    gridGap: theme.spacing(2),
    marginTop: 'var(--coa-section-spacing)',
  },
  review: {
    display: 'grid',
    gridGap: theme.spacing(2, 4),
    padding: 'var(--coa-section-spacing) var(--coa-container-spacing)',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: {
      gridTemplateColumns: 'auto 1fr',
      padding: 'var(--coa-container-spacing)',
    },
  },
  footer: {
    display: 'none',
    marginTop: 'var(--coa-section-spacing)',
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: {
      display: 'flex',
    },
    justifyContent: 'center',
  },
  readMore: {
    display: 'flex',
    marginTop: 'var(--coa-section-spacing)',
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: {
      display: 'none',
    },
    justifyContent: 'center',
  },
  moreButton: {
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: {
      maxWidth: 325,
    },
  },
}))

function Reviews(props) {
  const { productCode, productTitle, productCodes } = props
  const classes = useStyles(props)

  const reviewsPerPage = 3
  const formattedCodes = productCodes.trim().replaceAll(' ', '')

  const { reviews, totalScore, totalReviews, page, totalPages, onChangePage } = useReviews(
    formattedCodes,
    reviewsPerPage,
  )

  const t = useTranslations()

  const isBreakpointUp = useMediaQuery((theme) => theme.breakpoints.up('sm'))
  const transitionComponent = !isBreakpointUp ? TransitionComponent : undefined

  const [error, setError] = React.useState()
  const [isSubmitted, setSubmitted] = React.useState(false)
  const [isDialogOpen, setDialogOpen] = React.useState(false)
  const handleDialogToggle = React.useCallback(() => {
    setDialogOpen((prev) => !prev)
  }, [])

  const [readMoreReviews, setReadMoreReviews] = React.useState(reviews)
  const [readMore, setReadMore] = React.useState(false)

  const handleSubmit = React.useCallback(async (values, { setSubmitting }) => {
    setSubmitting(true)
    try {
      const success = await submitProductReview(values)

      if (success) {
        setSubmitted(true)
      } else {
        setError('Could not create review')
      }
    } catch (err) {
      console.error(err)
      setError(err.message)
    }
    setSubmitting(false)
  }, [])

  const router = useRouter()

  React.useEffect(() => {
    const reviewsOnPage = document.querySelector('#reviews')

    if (
      window.innerWidth >= 600 &&
      reviewsOnPage &&
      Object.keys(router.query).includes('reviews')
    ) {
      reviewsOnPage.scrollIntoView({ behavior: 'smooth' })
    }
  }, [reviews, router])

  const handlePageChange = React.useCallback(
    (event, pageProp) => {
      onChangePage(pageProp)
    },
    [onChangePage],
  )

  const handleReadMore = React.useCallback(
    (e, pageNum) => {
      e.preventDefault()
      onChangePage(pageNum + 1)
    },
    [onChangePage],
  )

  React.useEffect(() => {
    if (reviews?.length > 0) {
      setReadMoreReviews((prev) => [...prev, ...reviews])
    }
  }, [reviews])

  React.useEffect(() => {
    const handleResize = () => {
      if (window && window.innerWidth <= 600) setReadMore(true)
      else setReadMore(false)
    }

    window.addEventListener('resize', handleResize)

    if (window.innerWidth <= 600) setReadMore(true)
    else setReadMore(false)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const Article = ({ review }) => {
    return (
      <article className={classes.review}>
        <div>
          <Rating value={review?.score ?? 0} readOnly />
          <Typography component="p" variant="subtitle1">
            {review?.name || ''}
          </Typography>
        </div>

        <Typography>{review?.content || ''}</Typography>
      </article>
    )
  }

  Article.propTypes = {
    review: PropTypes.object,
  }

  return (
    <>
      <Section className={classes.root} gutters="padding" id="reviews">
        <Container className={classes.container} maxWidth="md">
          <header className={classes.header}>
            <Typography className={classes.heading} component="h1" variant="h6" gutterBottom>
              {t('Web.Reviews.Title')}
            </Typography>

            <div className={classes.reviewInfo}>
              <Rating value={totalScore} readOnly />
              <Typography color="textSecondary" component="span" variant="subtitle2">
                {totalReviews} {t('Web.Reviews.Total')}
              </Typography>

              {productCode && (
                <Link // eslint-disable-line jsx-a11y/anchor-is-valid
                  onClick={handleDialogToggle}
                  component="button"
                  type="button"
                  underline="always"
                  variant="subtitle2"
                >
                  {t('Web.Reviews.Write')}
                </Link>
              )}
            </div>
          </header>

          {reviews?.length > 0 ? (
            <div className={classes.main}>
              {readMore
                ? readMoreReviews?.map((review, idx) => <Article key={idx} review={review} />)
                : reviews?.map((review, idx) => <Article key={idx} review={review} />)}
            </div>
          ) : (
            <div className={classes.main}>
              <Typography> {t('Web.Reviews.NoReviews')}</Typography>
            </div>
          )}

          {totalReviews > reviewsPerPage && (
            <footer className={classes.footer}>
              <Pagination count={totalPages} page={page} onChange={handlePageChange} />
            </footer>
          )}

          {totalReviews > reviewsPerPage && (
            <footer className={classes.readMore}>
              <Button
                disabled={page === totalPages}
                variant="contained"
                onClick={(e) => handleReadMore(e, page)}
              >
                {t('Web.Global.Reviews.ReadMore')}
              </Button>
            </footer>
          )}
        </Container>
      </Section>

      <Formit
        initialValues={{
          name: '',
          email: '',
          rating: 5,
          title: '',
          message: '',
          wouldRecommend: '1',
          productCode,
          productTitle,
        }}
        onSubmit={handleSubmit}
      >
        <Dialog
          TransitionComponent={transitionComponent}
          fullScreen={!isBreakpointUp}
          onClose={handleDialogToggle}
          open={isDialogOpen}
          maxWidth="xs"
          fullWidth
        >
          {isSubmitted ? <Alert> {t('Web.Reviews.Submit.Message')}</Alert> : <ReviewsForm />}
          {error && <Alert severity="error">{error}</Alert>}
        </Dialog>
      </Formit>
    </>
  )
}

Reviews.propTypes = {
  productCode: PropTypes.string,
  productTitle: PropTypes.string,
  productCodes: PropTypes.string,
}

export default Reviews
